'use client'

import { useSyncExternalStore } from 'react'

export type GooglePreferredSourceStatus = 'unknown' | 'added'

function normalizeLabel(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFKC')
    .toLocaleLowerCase('tr-TR')
    .replace(/[’‘ʼʻ`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function isAddedLabel(value: string | null | undefined): boolean {
  const label = normalizeLabel(value)
  // These are the SDK's successful/already-added labels. Ineligible buttons
  // are also disabled, so disabled state alone cannot confirm a preference.
  return /^tercih edilen kaynaklar'?a eklendi[.!]?$/.test(label)
    || /^added to preferred sources[.!]?$/.test(label)
}

export function getGooglePreferredSourceButtonStatus(
  button: Element | null,
): GooglePreferredSourceStatus {
  if (!button) return 'unknown'
  const disabled = button.getAttribute('aria-disabled')?.trim().toLowerCase() === 'true'
    || button.hasAttribute('soft-disabled')
    || button.hasAttribute('disabled')
  if (!disabled) return 'unknown'
  const labels = [
    button.getAttribute('aria-label'),
    button.getAttribute('title'),
    button.querySelector('.publisher-btn-text')?.textContent,
    button.textContent,
  ]
  return labels.some(isAddedLabel) ? 'added' : 'unknown'
}

// Deliberately limited to this page's browser runtime: Google account changes
// and preferences removed elsewhere cannot be verified from browser storage.
let currentStatus: GooglePreferredSourceStatus = 'unknown'
const listeners = new Set<() => void>()
const getSnapshot = () => currentStatus
const getServerSnapshot = (): GooglePreferredSourceStatus => 'unknown'
const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function confirmGooglePreferredSourceAdded(): void {
  if (typeof window === 'undefined' || currentStatus === 'added') return
  currentStatus = 'added'
  for (const listener of Array.from(listeners)) listener()
}

export function useGooglePreferredSourceStatus(): GooglePreferredSourceStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
